/**
 * One chat's Transcript: first page, older pages, live refresh and send.
 * Many instances may exist at once (one per open chat); nothing is global.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { takePrefetched } from './prefetch';
import { CrmError, crmFetch, dataOf } from './api';
import { mergeMessages, messageId, parseMessagesPage, type ChatMessage, type MessageContext, type PageCursor } from './messages';
import { outgoingText } from './send';
import { buildSend, type Draft, type SendRefusal } from './send';

const PAGE = 50;

export type SendResult =
  | { ok: true; messageId?: string }
  | { ok: false; refused: SendRefusal }
  | { ok: false; confirmTerms: string[] }
  | { ok: false; error: string; partial?: boolean };

export interface Thread {
  messages: ChatMessage[];
  loading: boolean;
  loadingOlder: boolean;
  hasOlder: boolean;
  error: string | null;
  loadOlder: () => void;
  refresh: () => void;
  /**
   * Send a draft. It shows in the Transcript at once as "sending…" (`onQueued`
   * fires then, so the composer can clear) and turns into the real message
   * when OnlyFans answers; a failed send stays in place with Retry.
   */
  send: (draft: Omit<Draft, 'fanId'>, opts?: SendOptions) => Promise<SendResult>;
  /** Send a failed message again. */
  retry: (tempId: string) => void;
  /** Drop a failed message. */
  discard: (tempId: string) => void;
  /** Unsend one of the creator's messages (OnlyFans deletes it for the fan too). */
  unsend: (messageId: string) => Promise<void>;
}

export interface SendOptions {
  confirmed?: boolean;
  onQueued?: () => void;
}

let tempSeq = 0;

/** The message as the Transcript should show it while it is on its way. */
export function pendingMessage(draft: Omit<Draft, 'fanId'>, id: string): ChatMessage {
  const price = draft.price != null && draft.price > 0 ? draft.price : 0;
  const content = draft.media.filter((m) => !m.voice);
  const paid = price > 0 && (content.length > 0 || !!draft.text.trim());
  return {
    id,
    side: 'creator',
    sender: { kind: 'me' },
    text: outgoingText(draft.text, draft.replyTo),
    createdAt: Date.now(),
    media: draft.media.map((m) => ({
      id: m.id,
      type: m.voice ? 'audio' : m.type || 'photo',
      thumb: m.thumb ?? null,
      src: null,
      canView: true,
      preview: paid && !!m.preview,
    })),
    ppv: paid ? { price, bought: false } : null,
    tip: null,
    isRead: null,
    giphyId: draft.giphyId ?? null,
    pending: { state: 'sending' },
  };
}

function cursorBody(cursor: PageCursor): Record<string, unknown> {
  if (!cursor) return { offset: 0 };
  if ('offset' in cursor) return { offset: cursor.offset };
  const id = Number(cursor.beforeId) || cursor.beforeId;
  return { message_id: id, marker_id: cursor.beforeId, marker_message_id: cursor.beforeId, last_message_id: id };
}

/** The newest page of a chat as the CRM returns it (also what the list prefetches on hover). */
export function fetchNewestBody(accountId: string, fanId: string, signal?: AbortSignal): Promise<unknown> {
  return crmFetch('/v1/messages/get', { accountId, body: { fan_id: fanId, limit: PAGE }, signal });
}

export function useThread(accountId: string, fanId: string, myUsername: string): Thread {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [next, setNext] = useState<PageCursor>(null);
  const [error, setError] = useState<string | null>(null);
  const ctx = useRef<MessageContext>({ fanId, myUsername });
  ctx.current = { fanId, myUsername };
  const chatKey = `${accountId}:${fanId}`;
  const keyRef = useRef(chatKey);
  keyRef.current = chatKey;

  const fetchPage = useCallback(
    async (cursor: PageCursor, signal?: AbortSignal) => {
      const body = await crmFetch('/v1/messages/get', {
        accountId,
        body: { fan_id: fanId, limit: PAGE, ...cursorBody(cursor) },
        signal,
      });
      return parseMessagesPage(body, ctx.current);
    },
    [accountId, fanId],
  );

  useEffect(() => {
    const controller = new AbortController();
    setMessages([]);
    setNext(null);
    setError(null);
    setLoading(true);
    // Hovering the chat in the list may have fetched this page already.
    const prefetched = takePrefetched(accountId, fanId);
    const first = prefetched
      ? prefetched.then((body) => (body ? parseMessagesPage(body, ctx.current) : fetchPage(null, controller.signal)))
      : fetchPage(null, controller.signal);
    first
      .then((page) => {
        setMessages(page.messages);
        setNext(page.next);
      })
      .catch((err) => {
        if ((err as { name?: string }).name !== 'AbortError') setError((err as Error).message);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [fetchPage, accountId, fanId]);

  const olderBusy = useRef(false);
  const loadOlder = useCallback(() => {
    if (!next || olderBusy.current) return;
    olderBusy.current = true;
    setLoadingOlder(true);
    const forKey = keyRef.current;
    fetchPage(next)
      .then((page) => {
        if (keyRef.current !== forKey) return;
        setMessages((prev) => mergeMessages(prev, page.messages));
        // History ends when the cursor stops moving (CRM ADR 0001), not on page size.
        const stuck = page.next && next && JSON.stringify(page.next) === JSON.stringify(next);
        setNext(stuck ? null : page.next);
      })
      .catch(() => undefined)
      .finally(() => {
        olderBusy.current = false;
        setLoadingOlder(false);
      });
  }, [fetchPage, next]);

  const refresh = useCallback(() => {
    const forKey = keyRef.current;
    fetchPage(null)
      .then((page) => {
        if (keyRef.current === forKey) setMessages((prev) => mergeMessages(prev, page.messages));
      })
      .catch(() => undefined);
  }, [fetchPage]);

  const drafts = useRef(new Map<string, Omit<Draft, 'fanId'>>());
  useEffect(() => drafts.current.clear(), [chatKey]);

  const patch = useCallback((id: string, next: Partial<ChatMessage> | null) => {
    setMessages((prev) => (next ? prev.map((m) => (m.id === id ? { ...m, ...next } : m)) : prev.filter((m) => m.id !== id)));
  }, []);

  const deliver = useCallback(
    async (tempId: string, draft: Omit<Draft, 'fanId'>, opts: SendOptions): Promise<SendResult> => {
      const built = buildSend({ ...draft, fanId });
      if (!built.ok) return { ok: false, refused: built.reason };
      const forKey = keyRef.current;
      let sent = 0;
      let realId = '';
      try {
        for (const req of built.requests) {
          const body = await crmFetch(req.path, {
            accountId,
            body: opts.confirmed ? { ...req.body, _crm_prohibited_confirmed: true } : req.body,
          });
          if (!sent) realId = messageId((dataOf(body) || {}) as Record<string, unknown>);
          sent += 1;
        }
      } catch (err) {
        if (keyRef.current !== forKey) return { ok: false, error: (err as Error).message };
        if (err instanceof CrmError && err.code === 'prohibited_terms_confirmation_required') {
          // Nothing went out: the draft goes back to the composer for a decision.
          patch(tempId, null);
          drafts.current.delete(tempId);
          const terms = (err.body as { terms?: unknown } | null)?.terms;
          return { ok: false, confirmTerms: Array.isArray(terms) ? terms.map(String) : [] };
        }
        if (sent) {
          // The message itself went out; only the GIF or voice note after it failed.
          if (/^\d+$/.test(realId)) patch(tempId, { id: realId, pending: undefined });
          else window.setTimeout(() => patch(tempId, null), 2600);
          drafts.current.delete(tempId);
          refresh();
          return { ok: false, partial: true, error: (err as Error).message };
        }
        patch(tempId, { pending: { state: 'failed', error: (err as Error).message } });
        return { ok: false, error: (err as Error).message };
      }
      drafts.current.delete(tempId);
      if (keyRef.current === forKey) {
        // The real id makes the next page replace this copy instead of doubling it.
        if (/^\d+$/.test(realId)) patch(tempId, { id: realId, pending: undefined });
        else window.setTimeout(() => patch(tempId, null), 2600);
        refresh();
        // OnlyFans can take a moment to list a fresh send: look again shortly.
        window.setTimeout(refresh, 2500);
      }
      return { ok: true, messageId: /^\d+$/.test(realId) ? realId : undefined };
    },
    [accountId, fanId, patch, refresh],
  );

  const send = useCallback(
    async (draft: Omit<Draft, 'fanId'>, opts: SendOptions = {}): Promise<SendResult> => {
      const built = buildSend({ ...draft, fanId });
      if (!built.ok) return { ok: false, refused: built.reason };
      const tempId = `tmp-${Date.now()}-${++tempSeq}`;
      drafts.current.set(tempId, draft);
      setMessages((prev) => [...prev, pendingMessage(draft, tempId)]);
      opts.onQueued?.();
      return deliver(tempId, draft, opts);
    },
    [deliver, fanId],
  );

  const retry = useCallback(
    (tempId: string) => {
      const draft = drafts.current.get(tempId);
      if (!draft) return;
      patch(tempId, { pending: { state: 'sending' }, createdAt: Date.now() });
      void deliver(tempId, draft, { confirmed: true });
    },
    [deliver, patch],
  );

  const discard = useCallback(
    (tempId: string) => {
      drafts.current.delete(tempId);
      patch(tempId, null);
    },
    [patch],
  );

  const unsend = useCallback(
    async (messageId: string) => {
      await crmFetch('/v1/messages/delete', { accountId, body: { fan_id: fanId, message_id: messageId } });
      setMessages((prev) => prev.filter((m) => m.id !== messageId));
      window.setTimeout(refresh, 1500);
    },
    [accountId, fanId, refresh],
  );

  return { messages, loading, loadingOlder, hasOlder: !!next, error, loadOlder, refresh, send, retry, discard, unsend };
}
